import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Eye, TrendingUp, Megaphone, UserCircle, Trophy, FileText, CheckCircle2,
  Ticket, Sparkles, Instagram, Music2, Lock, Link2,
} from "lucide-react";
import { StatCard, SectionCard, Tabs, fmt } from "./parts";
import { TrendChart } from "./charts";

const TABS = [
  { key: "overview", label: "Overview", Icon: TrendingUp },
  { key: "campaigns", label: "Campaigns", Icon: Megaphone },
  { key: "creators", label: "Creators", Icon: UserCircle },
];

const PHASE = { active: "bg-emerald-100 text-emerald-700", upcoming: "bg-sky-100 text-sky-700", completed: "bg-zinc-100 text-zinc-600" };
const PLATFORM_ICON = { instagram: Instagram, tiktok: Music2 };

function SocialInsights({ social, onConnect }) {
  if (social?.connected && social.platforms?.length) {
    const followersTrend = social.trends?.followers || [];
    const reachTrend = social.trends?.reach || [];
    return (
      <SectionCard testid="social-insights-live" title="Social insights" subtitle="Synced from your connected accounts">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {social.platforms.map((p) => {
            const Icon = PLATFORM_ICON[p.platform] || Instagram;
            return (
              <div key={p.platform} data-testid={`social-platform-${p.platform}`} className="rounded-2xl border border-zinc-100 bg-white p-4">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-rose-500" />
                  <span className="text-sm font-semibold text-zinc-700">@{p.username}</span>
                </div>
                <p className="mt-2 font-display text-2xl font-bold text-zinc-900">{fmt(p.followers)}</p>
                <p className="text-xs text-zinc-400">followers{p.engagement_rate != null ? ` · ${p.engagement_rate}% eng.` : ""}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          {followersTrend.length > 1 && (
            <div data-testid="social-trend-followers">
              <p className="mb-1 text-sm font-semibold text-zinc-700">Follower growth</p>
              <TrendChart data={followersTrend} color="#059669" />
            </div>
          )}
          {reachTrend.length > 1 && (
            <div data-testid="social-trend-reach">
              <p className="mb-1 text-sm font-semibold text-zinc-700">Reach growth</p>
              <TrendChart data={reachTrend} color="#0EA5E9" />
            </div>
          )}
        </div>
      </SectionCard>
    );
  }
  return (
    <div data-testid="social-insights-locked" className="relative overflow-hidden rounded-3xl border border-dashed border-zinc-300 bg-white p-6 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-900 text-white"><Lock className="h-5 w-5" /></div>
      <h3 className="mt-3 font-display text-lg font-bold text-zinc-900">Follower, reach &amp; audience insights</h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-zinc-500">
        Connect Instagram or TikTok to unlock verified follower growth, reach over time and audience demographics. We never show estimated or sample numbers here.
      </p>
      <button data-testid="social-connect-cta" onClick={onConnect} className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700">
        <Link2 className="h-4 w-4" /> Connect a platform
      </button>
    </div>
  );
}

function pctChange(from, to) {
  if (from == null || to == null || from === 0) return null;
  return Math.round(((to - from) / from) * 100);
}

function BeforeDuringAfter({ label, block, suffix = "", fmtVal = fmt }) {
  if (!block || block.during == null) return null;
  const vals = [block.before, block.during, block.after].filter((v) => v != null);
  const max = Math.max(...vals, 1);
  const bars = [
    { k: "Before", v: block.before, c: "bg-zinc-300" },
    { k: "During", v: block.during, c: "bg-emerald-500" },
    { k: "After", v: block.after, c: "bg-indigo-500" },
  ];
  const change = pctChange(block.before, block.during);
  return (
    <div data-testid={`bda-${label.toLowerCase()}`} className="rounded-2xl border border-zinc-100 bg-white p-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-zinc-700">{label}</p>
        {change != null && (
          <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-semibold ${change >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
            {change >= 0 ? "+" : ""}{change}% during
          </span>
        )}
      </div>
      <div className="mt-3 space-y-2">
        {bars.map((b) => (
          <div key={b.k} className="flex items-center gap-2">
            <span className="w-14 shrink-0 text-xs text-zinc-400">{b.k}</span>
            <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-zinc-100">
              <div className={`h-full rounded-full ${b.c}`} style={{ width: `${b.v != null ? (b.v / max) * 100 : 0}%` }} />
            </div>
            <span className="w-16 shrink-0 text-right font-mono text-xs font-semibold text-zinc-900">{b.v != null ? `${fmtVal(b.v)}${suffix}` : "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CampaignPerformance({ data, onConnect }) {
  const perf = data.campaign_performance || [];
  const connected = data.social?.connected;
  if (!connected || perf.length === 0) {
    return (
      <div data-testid="campaign-performance-locked" className="rounded-3xl border border-dashed border-zinc-300 bg-white p-6 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-900 text-white"><Lock className="h-5 w-5" /></div>
        <h3 className="mt-3 font-display text-lg font-bold text-zinc-900">Campaign performance</h3>
        <p className="mx-auto mt-1 max-w-md text-sm text-zinc-500">
          Connect Instagram to unlock campaign analytics — you'll see how your reach, followers and engagement changed before, during and after each campaign window.
        </p>
        <button data-testid="campaign-perf-connect-cta" onClick={onConnect} className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
          <Link2 className="h-4 w-4" /> Connect Instagram
        </button>
      </div>
    );
  }
  return (
    <SectionCard testid="campaign-performance" title="Campaign performance" subtitle="Reach, followers &amp; engagement observed before, during and after each campaign window">
      <div className="space-y-5">
        {perf.map((c) => (
          <div key={c.campaign_id} data-testid={`campaign-perf-${c.campaign_id}`} className="rounded-2xl bg-zinc-50 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-display font-bold text-zinc-900">{c.name}</p>
              <span className="text-xs text-zinc-400">{c.start_date} → {c.end_date}</span>
            </div>
            <div className="mt-3 grid gap-3 lg:grid-cols-3">
              <BeforeDuringAfter label="Reach" block={c.reach} />
              <BeforeDuringAfter label="Followers" block={c.followers} />
              <BeforeDuringAfter label="Engagement" block={c.engagement} suffix="%" fmtVal={(v) => v} />
            </div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-zinc-400">
        These are changes observed during each campaign window from your connected accounts — helpful signal, but not a guarantee the campaign alone caused them.
      </p>
    </SectionCard>
  );
}

function EmptyState({ title, body, cta, onCta, testid }) {
  return (
    <div data-testid={testid} className="rounded-3xl border border-dashed border-zinc-300 bg-white p-10 text-center">
      <p className="font-display text-lg font-bold text-zinc-800">{title}</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-zinc-500">{body}</p>
      {cta && <button onClick={onCta} className="mt-4 rounded-full bg-zinc-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-zinc-800">{cta}</button>}
    </div>
  );
}

function OpportunityPerformance({ rows, onCreate }) {
  if (!rows.length) {
    return (
      <EmptyState testid="opp-perf-empty" title="No opportunities yet"
        body="Post your first opportunity and you'll see per-listing views, applications, acceptances and conversion here."
        cta="Create an opportunity" onCta={onCreate} />
    );
  }
  return (
    <SectionCard testid="opportunity-performance" title="Opportunity performance" subtitle="Views, applications and conversion for each listing">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-zinc-400">
              <th className="pb-2 pr-3 font-medium">Opportunity</th>
              <th className="pb-2 px-3 text-right font-medium">Views</th>
              <th className="pb-2 px-3 text-right font-medium">Applications</th>
              <th className="pb-2 px-3 text-right font-medium">Accepted</th>
              <th className="pb-2 px-3 text-right font-medium">Conversion</th>
              <th className="pb-2 pl-3 text-right font-medium">Content</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 20).map((o) => (
              <tr key={o.id} data-testid={`opp-perf-${o.id}`} className="border-t border-zinc-100">
                <td className="py-2.5 pr-3">
                  <p className="truncate font-medium text-zinc-900">{o.title}</p>
                  <p className="text-xs capitalize text-zinc-400">{o.status}</p>
                </td>
                <td className="px-3 text-right font-mono text-zinc-900">{fmt(o.views)}</td>
                <td className="px-3 text-right font-mono text-zinc-900">{fmt(o.applications)}</td>
                <td className="px-3 text-right font-mono text-zinc-900">{fmt(o.accepted)}</td>
                <td className="px-3 text-right font-mono text-zinc-600">{o.conversion_rate != null ? `${o.conversion_rate}%` : "—"}</td>
                <td className="pl-3 text-right font-mono text-zinc-600">{fmt(o.content_submissions)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

export function BusinessAnalytics({ data, onConnect }) {
  const [tab, setTab] = useState("overview");
  const navigate = useNavigate();
  const a = data.activity;
  const totalViews = (a.opportunity_views || 0) + (a.public_views || 0);
  const bestCreator = data.creators?.[0];

  return (
    <div className="space-y-6">
      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {tab === "overview" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard testid="stat-opportunities" label="Opportunities posted" metric={{ value: a.opportunities_posted }} Icon={FileText} tint="text-violet-600 bg-violet-100" index={0} />
            <StatCard testid="stat-applications" label="Applications received" metric={{ value: a.applications_received }} Icon={Users} tint="text-sky-600 bg-sky-100" index={1} />
            <StatCard testid="stat-creators" label="Creators worked with" metric={{ value: a.creators_worked }} Icon={UserCircle} tint="text-emerald-600 bg-emerald-100" index={2} />
            <StatCard testid="stat-views" label="Total listing views" metric={{ value: totalViews }} Icon={Eye} tint="text-indigo-600 bg-indigo-100" index={3} />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard testid="stat-campaigns" label="Campaigns run" metric={{ value: a.campaigns_run }} Icon={Megaphone} tint="text-fuchsia-600 bg-fuchsia-100" index={0} />
            <StatCard testid="stat-collabs-completed" label="Collaborations completed" metric={{ value: a.collaborations_completed }} Icon={CheckCircle2} tint="text-emerald-600 bg-emerald-100" index={1} />
            <StatCard testid="stat-events" label="Events hosted" metric={{ value: a.events_hosted }} Icon={Ticket} tint="text-fuchsia-600 bg-fuchsia-100" index={2} />
            <StatCard testid="stat-event-reach" label="Event creator reach" metric={{ value: a.event_content_reach }} Icon={Trophy} tint="text-amber-600 bg-amber-100" index={3} />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard testid="stat-conversion" label="Application conversion" metric={{ value: a.conversion_rate != null ? `${a.conversion_rate}%` : "—" }} Icon={TrendingUp} tint="text-sky-600 bg-sky-100" index={0} />
            <StatCard testid="stat-creators-accepted" label="Accepted from applications" metric={{ value: a.creators_accepted ?? 0 }} Icon={CheckCircle2} tint="text-emerald-600 bg-emerald-100" index={1} />
            <StatCard testid="stat-active-collabs" label="Active collaborations" metric={{ value: a.collaborations_active ?? 0 }} Icon={Sparkles} tint="text-violet-600 bg-violet-100" index={2} />
            <StatCard testid="stat-content" label="Content submitted" metric={{ value: a.content_submissions ?? 0 }} Icon={FileText} tint="text-fuchsia-600 bg-fuchsia-100" index={3} />
          </div>
          {a.conversion_rate == null && (
            <p className="-mt-3 text-xs text-zinc-400">Conversion rate appears once your opportunities start getting views.</p>
          )}
          {a.event_content_reach > 0 && (
            <p className="-mt-3 text-xs text-zinc-400">Event creator reach is self-reported by attending creators.</p>
          )}

          <SectionCard testid="business-activity-trend" title="Activity — last 90 days" subtitle={`${a.applications_received} application${a.applications_received === 1 ? "" : "s"} across ${a.opportunities_posted} listing${a.opportunities_posted === 1 ? "" : "s"}`}>
            {(data.series?.applications || []).some((p) => p.value > 0) ? (
              <TrendChart data={data.series.applications} color="#0EA5E9" />
            ) : (
              <div className="flex h-40 items-center justify-center text-sm text-zinc-400">No applications in the last 90 days yet.</div>
            )}
          </SectionCard>

          <OpportunityPerformance rows={data.opportunity_performance || []} onCreate={() => navigate("/business/opportunities/create")} />

          <SocialInsights social={data.social} onConnect={onConnect} />
        </div>
      )}

      {tab === "campaigns" && (
        <div className="space-y-4">
          <CampaignPerformance data={data} onConnect={onConnect} />
          {data.campaigns?.length ? data.campaigns.map((c) => (
            <div key={c.id} data-testid={`campaign-card-${c.id}`} className="rounded-2xl border border-zinc-100 bg-white p-4">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-display font-bold text-zinc-900">{c.name}</p>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${PHASE[c.phase] || PHASE.completed}`}>{c.phase}</span>
                {(c.start_date || c.end_date) && <span className="ml-auto text-xs text-zinc-400">{c.start_date || "—"} → {c.end_date || "—"}</span>}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Metric label="Opportunities" value={c.opportunity_count} />
                <Metric label="Collaborations" value={c.collaboration_count} />
                <Metric label="Creators" value={c.creator_count} />
                <Metric label="Event reach" value={c.event_reach ? fmt(c.event_reach) : "—"} accent={c.event_reach > 0} />
              </div>
            </div>
          )) : (
            <EmptyState testid="campaigns-empty" title="No campaigns yet"
              body="Group related opportunities into a campaign to track their combined performance over time."
              cta="Manage campaigns" onCta={() => navigate("/business/campaigns")} />
          )}
        </div>
      )}

      {tab === "creators" && (
        <div className="space-y-6">
          {bestCreator && (bestCreator.event_reach > 0 || bestCreator.followers > 0) && (
            <div data-testid="best-creator" className="flex items-center gap-4 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5">
              {bestCreator.photo ? <img src={bestCreator.photo} alt="" referrerPolicy="no-referrer" className="h-14 w-14 rounded-2xl object-cover" /> : <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 font-display font-bold text-amber-600">{bestCreator.name?.[0]}</div>}
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-amber-600"><Sparkles className="h-3.5 w-3.5" /> Top creator</p>
                <p className="truncate font-display text-lg font-bold text-zinc-900">{bestCreator.name}</p>
                <p className="text-sm text-zinc-500">
                  {bestCreator.collaborations} collaboration{bestCreator.collaborations === 1 ? "" : "s"}
                  {bestCreator.event_reach > 0 ? ` · ${fmt(bestCreator.event_reach)} event reach` : ""}
                </p>
              </div>
            </div>
          )}
          {data.creators?.length ? (
            <SectionCard testid="business-creators" title="Creator roster" subtitle="Everyone who's collaborated with you">
              <div className="space-y-3">
                {data.creators.map((cr) => (
                  <div key={cr.user_id} data-testid={`creator-perf-${cr.user_id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-zinc-100 bg-white p-4">
                    {cr.photo ? <img src={cr.photo} alt="" referrerPolicy="no-referrer" className="h-11 w-11 rounded-xl object-cover" /> : <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-100 font-display font-bold text-zinc-400">{cr.name?.[0]}</div>}
                    <div className="min-w-0 flex-1">
                      <button onClick={() => navigate(`/creators/${cr.user_id}`)} className="truncate font-semibold text-zinc-900 hover:text-emerald-600">{cr.name}</button>
                      <p className="truncate text-xs text-zinc-500">{cr.niche || "Creator"} · {cr.collaborations} collaboration{cr.collaborations === 1 ? "" : "s"}</p>
                    </div>
                    <div className="flex gap-4 text-right text-sm">
                      <div><p className="font-mono font-bold text-zinc-900">{cr.followers ? fmt(cr.followers) : "—"}</p><p className="text-xs text-zinc-400">followers*</p></div>
                      <div><p className="font-mono font-bold text-fuchsia-600">{cr.event_reach ? fmt(cr.event_reach) : "—"}</p><p className="text-xs text-zinc-400">event reach</p></div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-zinc-400">*Follower counts are self-reported by creators until their accounts are connected via API.</p>
            </SectionCard>
          ) : (
            <EmptyState testid="creators-empty" title="No creators yet"
              body="Once creators apply and you accept them, or you invite them directly, they'll appear here with their collaboration history."
              cta="Discover creators" onCta={() => navigate("/discover")} />
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div>
      <p className={`font-mono text-lg font-bold ${accent ? "text-fuchsia-600" : "text-zinc-900"}`}>{value}</p>
      <p className="text-xs text-zinc-400">{label}</p>
    </div>
  );
}
